import { FlexBoxView } from '@play-co/timestep-core/flexbox';
import { BitmapTextAlign, View } from '@play-co/timestep-core/ui';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import sounds from 'src/lib/sounds';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import i18n from 'src/lib/i18n/i18n';

const footerLinks = [
  'privacy',
  'tos',
  'tosJpKk',
  'tokusyoho',
  'kessai',
  'rakutenpay',
  'purchaseInfo',
] as const;
type FooterLink = (typeof footerLinks)[number];

type LinkButtonOpts = {
  superview: View;
  localeText: () => string;
  onClick: () => void | Promise<void>;
  subFeature: string;
  labelHorizontalAlign: BitmapTextAlign;
};

const linkColor = '#53AAF4';

class LinkButton extends ButtonScaleViewWithText {
  private readonly underline: View;
  private align: BitmapTextAlign;
  private textWidthCache = 0;

  constructor(opts: LinkButtonOpts) {
    super({
      ...opts,
      width: 250,
      height: 45,
      fontSize: 22,
      font: 'Body',
      textColor: linkColor,
      labelPaddingX: 0,
      labelPaddingY: 0,
      onClick: async () => {
        sounds.sfx.play('ok');
        await opts.onClick();
      },
      tracking: {
        feature: 'settings',
        $subFeature: opts.subFeature,
      },
    });

    this.underline = new View({
      superview: this,
      height: 2,
      y: this.style.height - 9,
      backgroundColor: linkColor,
    });

    this.align = opts.labelHorizontalAlign;
  }

  public tick() {
    const labelWidth = this.label.getTextWidth();

    // Skip if the text width hasn't changed
    if (this.textWidthCache === labelWidth) return;

    let x = 0;

    // Calculate the horizontal reposition according to the label alignment
    if (this.align === 'right') {
      x = this.style.width - labelWidth;
    } else if (this.align === 'center') {
      x = (this.style.width - labelWidth) / 2;
    }

    // Update the underline position and width
    this.underline.updateOpts({
      width: labelWidth,
      x,
    });

    this.textWidthCache = labelWidth;
  }
}

export type FooterLinksOpts = {
  superview: View;
  items: FooterLink[];
};

export class FooterLinks extends FlexBoxView {
  constructor(opts: FooterLinksOpts) {
    const useSingleColumn = opts.items.length <= 3;

    super({
      superview: opts.superview,
      flex: {
        width: '100%',
        flexDirection: useSingleColumn ? 'column' : 'row',
        justifyContent: useSingleColumn ? 'center' : 'space-between',
        alignContent: 'center',
        flexWrap: 'wrap',
        paddingHorizontal: 36,
        paddingBottom: 20,
      },
    });

    opts.items.forEach((type, index) => {
      const align = useSingleColumn
        ? 'center'
        : index % 2 === 0
          ? 'left'
          : 'right';
      this.createLinkButton(type, align);
    });
  }

  private createLinkButton(
    type: FooterLink,
    labelHorizontalAlign: BitmapTextAlign,
  ) {
    let opts: Omit<LinkButtonOpts, 'superview'>;

    switch (type) {
      case 'privacy':
        opts = {
          labelHorizontalAlign,
          localeText: () => i18n('platform.line.terms.pp.settingsBtn'),
          subFeature: 'popup-settings-terms-pp',
          onClick: this.onFooterLinkClick.bind(this, 'privacy'),
        };
        break;
      case 'tos':
        opts = {
          labelHorizontalAlign,
          localeText: () => i18n('platform.line.terms.tos.settingsBtn'),
          subFeature: 'popup-settings-terms-tos',
          onClick: this.onFooterLinkClick.bind(this, 'tos'),
        };
        break;
      case 'tosJpKk':
        opts = {
          labelHorizontalAlign,
          localeText: () => i18n('platform.line.terms.tosJpKk.settingsBtn'),
          subFeature: 'popup-settings-terms-tosJpKk',
          onClick: this.onFooterLinkClick.bind(this, 'tosJpKk'),
        };
        break;
      case 'tokusyoho':
        opts = {
          labelHorizontalAlign,
          localeText: () => i18n('platform.line.terms.tokusyoho.settingsBtn'),
          subFeature: 'popup-settings-terms-tokusyoho',
          onClick: this.onFooterLinkClick.bind(this, 'tokusyoho'),
        };
        break;
      case 'kessai':
        opts = {
          labelHorizontalAlign,
          localeText: () => i18n('platform.line.terms.kessai.settingsBtn'),
          subFeature: 'popup-settings-terms-kessai',
          onClick: this.onFooterLinkClick.bind(this, 'kessai'),
        };
        break;
      case 'rakutenpay':
        opts = {
          labelHorizontalAlign,
          localeText: () => '楽天ペイについて',
          subFeature: 'popup-settings-terms-rakutenpay',
          onClick: this.onFooterLinkClick.bind(this, 'rakutenpay'),
        };
        break;
      case 'purchaseInfo':
        opts = {
          labelHorizontalAlign,
          localeText: () => i18n('settings.purchaseInfo.title'),
          subFeature: 'popup-settings-purchase-information',
          onClick: this.onFooterLinkClick.bind(this, 'purchaseInfo'),
        };
        break;
    }

    new LinkButton({
      superview: this,
      ...opts,
    });
  }

  private async onFooterLinkClick(type: FooterLink) {
    switch (type) {
      case 'privacy':
        await openPopupPromise('popupTermsPolicyDetails', { mode: 'pp' });
        break;
      case 'tos':
        await openPopupPromise('popupTermsPolicyDetails', { mode: 'tos' });
        break;
      case 'tosJpKk':
        await openPopupPromise('popupTermsPolicyDetails', { mode: 'tosJPKk' });
        break;
      case 'tokusyoho':
        await openPopupPromise('popupTermsPolicyDetails', {
          mode: 'tokusyoho',
        });
        break;
      case 'kessai':
        await openPopupPromise('popupTermsPolicyDetails', { mode: 'kessai' });
        break;
      case 'rakutenpay':
        window.open('https://checkout.rakuten.co.jp/', '_blank')?.focus();
        break;
      case 'purchaseInfo':
        await openPopupPromise('popupSettingsPurchaseInfo', {});
        return;
    }
  }
}
